import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 3000);
  };

  const contactInfo = [
    {
      icon: Phone,
      title: 'Call Us',
      detail: '(234) 231 - 2123',
      subdDetail: 'Mon-Sun 8AM - 8PM',
      link: 'tel:+12342312123'
    },
    {
      icon: Mail,
      title: 'Email Us',
      detail: 'contact@perfectpandarepairs.com',
      subdDetail: 'Quick response guaranteed',
      link: 'mailto:contact@perfectpandarepairs.com'
    },
    {
      icon: MapPin,
      title: 'Visit Us',
      detail: '1440 70th St',
      subdDetail: 'City, State 12345',
      link: '#'
    },
    {
      icon: Clock,
      title: 'Business Hours',
      detail: 'Monday - Sunday',
      subdDetail: '8:00 AM - 8:00 PM',
      link: '#'
    }
  ];

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-slate-900 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.4 }}
          >
            <p className="text-white/60 font-medium mb-4 tracking-wide uppercase text-sm">Get In Touch</p>
            <h1 className="text-4xl lg:text-5xl font-light text-white mb-4 leading-tight">
              Contact
              <span className="block font-semibold">Total Repair UK</span>
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Have questions or need drainage assistance? Our expert team is ready to help you with professional solutions.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, index) => (
              <motion.a
                key={index}
                href={info.link}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white p-6 border-l-4 border-slate-900 shadow-sm hover:shadow-md transition-shadow group"
              >
                <div className="w-12 h-12 bg-slate-900 flex items-center justify-center mb-4">
                  <info.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-2">{info.title}</h3>
                <p className="text-slate-900 font-semibold mb-1">{info.detail}</p>
                <p className="text-slate-500 text-sm">{info.subdDetail}</p>
              </motion.a>
            ))}
          </div>

          {/* Main Contact Section */}
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <motion.div
              className="lg:col-span-3"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.4 }}
            >
              <div className="bg-white p-8 lg:p-12 shadow-sm border border-slate-200">
                <div className="mb-8">
                  <h2 className="text-3xl font-light text-slate-900 mb-2">
                    Send Us a <span className="font-semibold">Message</span>
                  </h2>
                  <p className="text-slate-600">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>
                </div>

                {submitted ? (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="bg-green-50 border-l-4 border-green-500 p-8 text-center"
                  >
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <h3 className="text-xl font-semibold text-green-900 mb-2">Message Sent!</h3>
                    <p className="text-green-700">Thank you for contacting us. We'll respond shortly.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Full Name *</label>
                        <input
                          type="text"
                          placeholder="John Doe"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          required
                          className="h-12 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-slate-900 focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Email Address *</label>
                        <input
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          required
                          className="h-12 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-slate-900 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Phone Number</label>
                        <input
                          type="tel"
                          placeholder="(234) 231-2123"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          className="h-12 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-slate-900 focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-slate-700">Subject</label>
                        <input
                          type="text"
                          placeholder="How can we help?"
                          value={formData.subject}
                          onChange={(e) => setFormData({...formData, subject: e.target.value})}
                          className="h-12 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-slate-900 focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700">Your Message *</label>
                      <textarea
                        placeholder="Tell us about your drainage needs..."
                        value={formData.message}
                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                        required
                        className="h-32 w-full px-4 py-3 rounded-xl border border-slate-300 focus:border-slate-900 focus:outline-none resize-none transition-colors"
                      />
                    </div>
                    <button 
                      type="submit" 
                      className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 sm:px-8 sm:py-4 font-medium rounded-xl transition-colors flex items-center justify-center gap-2 mx-auto sm:w-auto w-full"
                    >
                      <Send className="w-4 h-4" />
                      Send Message
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

            {/* Sidebar Info */}
            <motion.div
              className="lg:col-span-2 space-y-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              {/* Quick Contact */}
              <div className="bg-slate-900 p-8 text-white">
                <h3 className="text-xl font-semibold mb-4">Quick Contact</h3>
                <p className="text-slate-300 mb-6 leading-relaxed">
                  Need drainage services? Contact us today for professional solutions.
                </p>
                <div className="space-y-4">
                  <a href="tel:+12342312123" className="block">
                    <div className="bg-white/10 border border-white/20 p-4 hover:bg-white/20 transition-colors">
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5" />
                        <div>
                          <p className="text-xs text-slate-300 uppercase tracking-wide mb-1">Call Us</p>
                          <p className="text-lg font-semibold">(234) 231-2123</p>
                        </div>
                      </div>
                    </div>
                  </a>
                  <a href="mailto:contact@perfectpandarepairs.com" className="block">
                    <div className="bg-white/10 border border-white/20 p-4 hover:bg-white/20 transition-colors">
                      <div className="flex items-center gap-3">
                        <Mail className="w-5 h-5" />
                        <div>
                          <p className="text-xs text-slate-300 uppercase tracking-wide mb-1">Email Us</p>
                          <p className="font-semibold">contact@perfectpandarepairs.com</p>
                        </div>
                      </div>
                    </div>
                  </a>
                </div>
              </div>

            </motion.div>
          </div>
        </div>
      </section>

    </div>
  );
}